import { useCallback, useMemo, useState } from 'react';
import { FeedbackRequest, FeedbackRequestSchema } from '../utils/zod/feedbackSchemas';
import { FeedbackService } from '../services/feedbackService';

export interface UseFeedbackFormState {
  values: FeedbackRequest;
  errors: Partial<Record<keyof FeedbackRequest, string>>;
  isSubmitting: boolean;
}

export function useFeedbackForm(initial?: Partial<FeedbackRequest>) {
  const [values, setValues] = useState<FeedbackRequest>({
    message: '',
    subject: undefined,
    category: undefined,
    email: '',
    ...initial,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FeedbackRequest, string>>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validate = useCallback((next: FeedbackRequest): boolean => {
    const result = FeedbackRequestSchema.safeParse(next);
    if (result.success) {
      setErrors({});
      return true;
    }
    const zodIssues = result.error.issues;
    const nextErrors: Partial<Record<keyof FeedbackRequest, string>> = {};
    for (const issue of zodIssues) {
      const path = issue.path?.[0] as keyof FeedbackRequest | undefined;
      if (path) nextErrors[path] = issue.message;
    }
    setErrors(nextErrors);
    return false;
  }, []);

  const setField = useCallback(<K extends keyof FeedbackRequest>(key: K, value: FeedbackRequest[K]) => {
    setValues(prev => ({ ...prev, [key]: value }));
  }, []);

  const canSubmit = useMemo(() => {
    if (!values.message || values.message.trim().length === 0) return false;
    if (!values.email || values.email.trim().length === 0) return false;
    if (isSubmitting) return false;
    return true;
  }, [values.message, values.email, isSubmitting]);

  const submit = useCallback(async () => {
    const next = { ...values, message: values.message.trim() };
    if (!validate(next)) return { success: false, error: 'Validation failed' } as const;
    setIsSubmitting(true);
    try {
      const res = await FeedbackService.sendFeedback(next);
      return res;
    } finally {
      setIsSubmitting(false);
    }
  }, [values, validate]);

  return {
    values,
    errors,
    isSubmitting,
    canSubmit,
    setField,
    validate,
    submit,
  } as const;
}


